import type { CSSProperties } from 'react'
import { iconRegistry, type IconName } from './iconNames'
import styles from './Icon.module.scss'

export interface IconProps {
  name: IconName
  /** Pixel size — sets both width and height. Default `20`. */
  size?: number
  /**
   * CSS color value applied to the SVG. Defaults to a token-driven gray that
   * flips automatically across light and dark themes. Pass `'currentColor'`
   * (or omit to inherit through CSS) for icons that should follow their
   * surrounding text color.
   */
  color?: string
  /** Stroke width for line-art icons (Sun / Moon / Monitor / Inbox). */
  strokeWidth?: number
  className?: string
  /**
   * If supplied, the icon is exposed to assistive tech as `role="img"`
   * with this label. Otherwise the icon is decorative and hidden via
   * `aria-hidden="true"`.
   */
  'aria-label'?: string
}

const DEFAULT_COLOR = 'var(--colors-foreground-gray-secondary, #424242)'

export function Icon({
  name,
  size = 20,
  color = DEFAULT_COLOR,
  strokeWidth = 1.75,
  className,
  'aria-label': ariaLabel,
}: IconProps) {
  const Component = iconRegistry[name]

  if (!Component) {
    if (import.meta.env.DEV) {
      console.warn(
        `[Icon] No SVG component registered for name "${name}". ` +
          `Add a .tsx file to src/components/atoms/Icon/Components/ ` +
          `and re-run \`npm run icons:generate\`.`,
      )
    }
    return null
  }

  // `color` propagates to inner paths via CSS `color:` + `currentColor` in
  // each SVG's fill/stroke. We forward it both as an inline style (so
  // `currentColor` resolves correctly) and as `fill` for legacy ORO icons
  // that read `props.fill || 'currentColor'` directly.
  const inlineStyle: CSSProperties = { color }

  const a11yProps =
    ariaLabel !== undefined
      ? ({ role: 'img', 'aria-label': ariaLabel } as const)
      : ({ 'aria-hidden': true } as const)

  return (
    <Component
      width={size}
      height={size}
      strokeWidth={strokeWidth}
      style={inlineStyle}
      className={[styles.icon, className].filter(Boolean).join(' ')}
      focusable={false}
      {...a11yProps}
    />
  )
}
